import { Workbox } from 'workbox-window';

type UpdateListener = (apply: () => void) => void;

let workbox: Workbox | null = null;
const listeners = new Set<UpdateListener>();
let pendingApply: (() => void) | null = null;
let lastCheck = 0;
let registered: Promise<unknown> = Promise.resolve();
let offered: ServiceWorker | null = null;
const minCheckGapMs = 60 * 1000;

export const buildId: string | null = import.meta.env.VITE_BUILD_ID ?? null;

export function isNewerBuild(serverBuild: string | undefined, current: string | null = buildId): boolean {
  return Boolean(serverBuild && current && current !== 'local' && serverBuild !== current);
}

export function checkForUpdate(): void {
  const wb = workbox;
  if (!wb || Date.now() - lastCheck < minCheckGapMs) return;
  lastCheck = Date.now();
  void registered.then(() => wb.update()).catch(() => undefined);
}

export function onUpdateAvailable(listener: UpdateListener): () => void {
  listeners.add(listener);
  if (pendingApply) listener(pendingApply);
  return () => listeners.delete(listener);
}

function offer(waiting: ServiceWorker): void {
  if (!navigator.serviceWorker.controller || offered === waiting) return;
  offered = waiting;
  pendingApply = () => {
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
      once: true,
    });
    waiting.postMessage({ type: 'SKIP_WAITING' });
  };
  for (const l of listeners) l(pendingApply);
}

function track(registration: ServiceWorkerRegistration, sw: ServiceWorker | null): void {
  sw?.addEventListener('statechange', () => {
    if (sw.state === 'installed' && registration.waiting) offer(registration.waiting);
  });
}

export function watchForWaiting(registration: ServiceWorkerRegistration): void {
  if (registration.waiting) offer(registration.waiting);
  track(registration, registration.installing);
  registration.addEventListener('updatefound', () => track(registration, registration.installing));
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  workbox = new Workbox('/sw.js', { scope: '/' });
  const registering = workbox.register();
  registered = registering;
  void registering.then(async (registration) => {
    if (!registration) return;
    watchForWaiting(registration);
    const periodic = (
      registration as unknown as {
        periodicSync?: { register: (tag: string, options: { minInterval: number }) => Promise<void> };
      }
    ).periodicSync;
    if (!periodic) return;
    try {
      const status = await navigator.permissions.query({
        name: 'periodic-background-sync' as PermissionName,
      });
      if (status.state === 'granted') await periodic.register('qs-refresh', { minInterval: 60 * 60 * 1000 });
    } catch {
      return;
    }
  });
  setInterval(checkForUpdate, 60 * 60 * 1000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  });
}

export function postToServiceWorker(message: Record<string, unknown>): void {
  void navigator.serviceWorker?.ready.then((r) => r.active?.postMessage(message)).catch(() => undefined);
}
